import {useState,useCallback,useContext,useRef} from 'react';
import {getFunctions,httpsCallable} from 'firebase/functions';
import {logEvent} from 'firebase/analytics';
import analytics from '../helpers/analytics';
import UserContext from '../contexts/UserContext';
import ErrorContext from '../contexts/ErrorContext';
import CreditsContext from '../contexts/CreditsContext';

const msgId=() => crypto.randomUUID();

// Event types from backend
const EventType={
    STARTED: 'started',
    THINKING: 'thinking',
    TOOL_CALL_START: 'tool_call_start',
    TOOL_CALL_RESULT: 'tool_call_result',
    ITERATION_COMPLETE: 'iteration_complete',
    RESPONSE: 'response',
    COMPLETE: 'complete',
    ERROR: 'error'
};

/**
 * useStreamingAI - Custom hook for streaming AI chat with real-time tool call updates
 * Uses Firebase onCall streaming with httpsCallable().stream()
 * Provides Cursor/Windsurf-style visibility into agent operations
 */
export default function useStreamingAI() {
    const [user]=useContext(UserContext);
    const setError=useContext(ErrorContext);
    const [credits,updateCredits]=useContext(CreditsContext);

    const [messages,setMessages]=useState([{id: msgId(),role: 'model',content: 'Hello'}]);
    const [isProcessing,setIsProcessing]=useState(false);
    const [pendingChanges,setPendingChanges]=useState(null);
    const [currentToolCalls,setCurrentToolCalls]=useState([]);
    const [thinkingState,setThinkingState]=useState(null);
    const [streamingMessage,setStreamingMessage]=useState(null);
    const abortControllerRef=useRef(null);

    // Get Firebase Functions instance
    const functions=getFunctions();

    /**
     * Format nodes for AI processing
     */
    const formatNodesForAI=useCallback((nodes) => {
        return nodes
            .filter(n => n.type!=='reccNode')
            .map((node) => ({
                id: node.id,
                type: node.type,
                data: {text: node.data.text},
                position: {x: node.position.x,y: node.position.y},
                width: node.style?.width,
                height: node.style?.height
            }));
    },[]);

    /**
     * Format edges for AI processing
     */
    const formatEdgesForAI=useCallback((edges) => {
        return edges.map((edge) => ({
            source: edge.source,
            sourceHandle: edge.sourceHandle?.slice(-1)||'c',
            target: edge.target,
            targetHandle: edge.targetHandle?.slice(-1)||'a',
            type: edge.type||'simplebezier'
        }));
    },[]);

    /**
     * Send message with streaming support using Firebase onCall streaming
     */
    const sendMessage=useCallback(async (message,options={}) => {
        const {nodes=[],edges=[],selectedNodes=[],onSuccess,onError,onToolCall,onThinking}=options;

        // Validate input
        if(!message||message.trim().length===0) {
            setError('Message cannot be empty');
            return;
        }

        // Check credits
        const freshCredits=await updateCredits();
        if(freshCredits===0) {
            setMessages(prev => [...prev,{
                id: msgId(),
                role: 'model',
                content: 'Sorry, you ran out of Flowsage AI credits. Please upgrade your plan.'
            }]);
            setError('No credits remaining. Please upgrade your plan.');
            return;
        }

        // Check size limits
        if(nodes.length>250||messages.length>500) {
            setError('Flowchart or chat history is too large.');
            return;
        }

        // Add user message to chat
        setMessages(prev => [...prev,{
            id: msgId(),
            role: 'user',
            user: user.displayName,
            content: message
        }]);

        setIsProcessing(true);
        setCurrentToolCalls([]);
        setThinkingState(null);
        setStreamingMessage(null);

        // Create AbortController for this request
        const controller=new AbortController();
        abortControllerRef.current=controller;

        try {
            // Build request payload
            const payload={
                message,
                nodes: formatNodesForAI(nodes),
                edges: formatEdgesForAI(edges),
                chatHistory: messages.slice(-10),
                selectedNodes: selectedNodes.map(n => ({id: n.id,data: n.data}))
            };

            // Get the callable function
            const agenticChatFn=httpsCallable(functions,'aiFunctions-agenticChat');

            // Call with streaming - returns { stream, data }
            const {stream,data}=await agenticChatFn.stream(payload);

            // Process streaming chunks as they arrive
            for await(const chunk of stream) {
                // Check if request was cancelled
                if(controller.signal.aborted) {
                    throw new DOMException('Request cancelled','AbortError');
                }
                // Each chunk has a 'type' field indicating the event type
                const eventType=chunk.type;

                switch(eventType) {
                    case EventType.STARTED:
                        break;

                    case EventType.THINKING:
                        setThinkingState(chunk);
                        if(onThinking) onThinking(chunk);
                        break;

                    case EventType.TOOL_CALL_START:
                        setCurrentToolCalls(prev => [...prev,{
                            ...chunk,
                            status: 'running'
                        }]);
                        if(onToolCall) onToolCall({type: 'start',...chunk});
                        break;

                    case EventType.TOOL_CALL_RESULT:
                        setCurrentToolCalls(prev => prev.map(tc =>
                            tc.id===chunk.id
                                ? {...tc,result: chunk.result,status: chunk.result?.error? 'error':'success'}
                                :tc
                        ));
                        if(onToolCall) onToolCall({type: 'result',...chunk});
                        break;

                    case EventType.ITERATION_COMPLETE:
                        break;

                    case EventType.RESPONSE:
                        setStreamingMessage(chunk.message);
                        break;

                    case EventType.COMPLETE:
                        // Will be handled by final data promise
                        break;

                    case EventType.ERROR:
                        throw new Error(chunk.error||'Unknown error');

                    default:
                }
            }

            // Wait for the final result
            const finalResult=await data;

            logEvent(analytics,'streaming_agentic_chat_completed');

            // Add AI response to chat with tool call history
            setMessages(prev => [...prev,{
                id: msgId(),
                role: 'model',
                content: finalResult.message,
                summary: finalResult.summary,
                iterations: finalResult.iterations,
                toolCalls: finalResult.toolCallHistory||currentToolCalls
            }]);

            // Set pending changes
            const changes=finalResult.changes;
            if(changes&&Object.keys(changes).some(key =>
                Array.isArray(changes[key])? changes[key].length>0:changes[key]
            )) {
                setPendingChanges(changes);
            }

            if(onSuccess) onSuccess(finalResult);
            return finalResult;

        } catch(error) {
            if(error.name==='AbortError') {
                setMessages(prev => [...prev,{
                    id: msgId(),
                    role: 'model',
                    content: 'Request cancelled.'
                }]);
                return;
            }

            console.error('[StreamingAI] Error:',error);
            setMessages(prev => [...prev,{
                id: msgId(),
                role: 'model',
                content: 'Sorry, there was an error processing your request.'
            }]);
            setError(error.message||'Failed to process AI request');
            if(onError) onError(error);

        } finally {
            setIsProcessing(false);
            setThinkingState(null);
        }
    },[messages,updateCredits,user,setError,formatNodesForAI,formatEdgesForAI,currentToolCalls,functions]);

    /**
     * Accept pending changes
     */
    const acceptChanges=useCallback(() => {
        setPendingChanges(null);
    },[]);

    /**
     * Reject pending changes
     */
    const rejectChanges=useCallback(() => {
        setPendingChanges(null);
        setError('Undo functionality coming soon. Please manually revert changes.');
    },[setError]);

    /**
     * Clear messages
     */
    const clearMessages=useCallback(() => {
        setMessages([{id: msgId(),role: 'model',content: 'Hello'}]);
        setPendingChanges(null);
        setCurrentToolCalls([]);
    },[]);

    /**
     * Load messages
     */
    const loadMessages=useCallback((newMessages) => {
        if(Array.isArray(newMessages)&&newMessages.length>0) {
            setMessages(newMessages);
        } else {
            setMessages([{id: msgId(),role: 'model',content: 'Hello'}]);
        }
        setPendingChanges(null);
    },[]);

    /**
     * Delete a message
     */
    const deleteMessage=useCallback((index) => {
        setMessages(prev => prev.filter((_,i) => i!==index));
    },[]);

    /**
     * Cancel request
     */
    const cancelRequest=useCallback(() => {
        if(abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current=null;
            setIsProcessing(false);
            setThinkingState(null);
        }
    },[]);

    return {
        messages,
        isProcessing,
        pendingChanges,
        currentToolCalls,
        thinkingState,
        streamingMessage,
        sendMessage,
        acceptChanges,
        rejectChanges,
        clearMessages,
        loadMessages,
        deleteMessage,
        cancelRequest,
        formatNodesForAI,
        formatEdgesForAI
    };
}
